/** Add / Edit Employee Log Work — port of the original logModal(). */
import { useState } from 'react';
import type { WorkLogInput } from '@/types/models';
import { today } from '@/domain/format';
import { LIMITS, validateWorkLog, type FieldErrors } from '@/domain/validation';
import { useAction, useDataset } from '@/hooks/data';
import { ErrorState, Field, LoadingState, ModalFrame } from '@/ui/kit';

export function WorkLogModal({ id, projectId, memberId, onClose }: { id?: string; projectId?: string; memberId?: string; onClose: () => void }) {
  const ds = useDataset();
  const existing = id ? ds.data?.logs.find((l) => l.id === id) : undefined;
  const title = `${id ? 'Edit' : 'Add'} Employee Log Work`;
  if (ds.error) return <ModalFrame title={title} onClose={onClose}><ErrorState error={ds.error} onRetry={ds.refetch} /></ModalFrame>;
  if (!ds.data) return <ModalFrame title={title} onClose={onClose}><LoadingState /></ModalFrame>;
  if (id && !existing) return <ModalFrame title={title} onClose={onClose}><div className="empty">No records found.</div></ModalFrame>;
  return <WorkLogForm key={id || 'new'} id={id} initial={existing ? { ...existing, hours: String(existing.hours) } : { date: today(), projectId: projectId || '', memberId: memberId || '', hours: '', work: '' }} onClose={onClose} title={title} projects={ds.data.projects} members={ds.data.members} />;
}

type FormState = Omit<WorkLogInput, 'hours'> & { hours: string };

function WorkLogForm({ id, initial, onClose, title, projects, members }: { id?: string; initial: FormState; onClose: () => void; title: string; projects: { id: string; name: string }[]; members: { id: string; name: string }[] }) {
  const [f, setF] = useState<FormState>(initial);
  const [err, setErr] = useState<FieldErrors>({});
  const set = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });
  const save = useAction(
    (s, rec: WorkLogInput) => (id ? s.workLogs.update(id, rec) : s.workLogs.create(rec)),
    id ? 'Work log updated successfully.' : 'Work logged successfully.',
  );
  const submit = async () => {
    const e = validateWorkLog(f);
    setErr(e);
    if (Object.keys(e).length) return;
    const ok = await save.run({ date: f.date, projectId: f.projectId, memberId: f.memberId, hours: Number(f.hours), work: f.work.trim() });
    if (ok) onClose();
  };
  return (
    <form onSubmit={(e) => (e.preventDefault(), void submit())} noValidate>
      <ModalFrame
        title={title}
        onClose={onClose}
        footer={
          <>
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn primary" disabled={save.pending}>
              {id ? 'Update' : 'Save'}
            </button>
          </>
        }
      >
        <div className="grid2">
          <Field label="Date" required error={err.date}>
            <input type="date" name="date" value={f.date} onChange={set('date')} />
          </Field>
          <Field label="Hours" required error={err.hours}>
            <input type="number" name="hours" value={f.hours} onChange={set('hours')} min={0} max={24} step={0.25} placeholder="e.g. 2.5" />
          </Field>
          <Field label="Member" required error={err.memberId}>
            <select name="memberId" value={f.memberId} onChange={set('memberId')}>
              <option value="">Select Member</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Project">
            <select name="projectId" value={f.projectId} onChange={set('projectId')}>
              <option value="">General (no project)</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Work done">
          <textarea name="work" maxLength={LIMITS.work} placeholder="What was done — this text goes into the report" value={f.work} onChange={set('work')} />
        </Field>
      </ModalFrame>
    </form>
  );
}
